import React, { useState, useRef, useCallback } from 'react';

import { SORT_DIR } from "@/constants/table";
import { type Table as TanstackTable, flexRender } from '@tanstack/react-table';
import { ClassValue } from 'clsx';
import { cn } from '@/lib/utils';
import { useAutoAnimate } from '@formkit/auto-animate/react';
interface HeadColumn {
    label: string;
    id: string;
    icon: string;
    isSort: boolean;
}

interface BodyColumn {
    colId: string;
    dataId: string;
}

interface BodyData {
    id: string;
    [key: string]: any;
}

interface TableProps {
    headCols: HeadColumn[];
    defaultSortId?: string;
    bodyCols: BodyColumn[];
    bodyData: BodyData[] | undefined | null;
    actionsColumn: (id: string) => React.ReactNode;
    getTableData?: ({ sortBy, sortDirection }: { sortBy: string, sortDirection: string }) => void;
    hasNextPage?: boolean | undefined;
    setPage?: React.Dispatch<React.SetStateAction<number>> | undefined;
    emptyTableMessage?: React.ReactNode;
    applyDefaultCellStyles?: boolean;
    tableClassName?: string;
}

export const Table: React.FC<TableProps> = ({
    headCols,
    defaultSortId = null,
    bodyCols,
    bodyData,
    actionsColumn,
    getTableData = () => { },
    hasNextPage = false,
    setPage = () => { },
    emptyTableMessage,
    applyDefaultCellStyles = true,
    tableClassName
}) => {
    const [sortDirection, setSortDirection] = useState(SORT_DIR.DESC);
    const [sortBy, setSortBy] = useState(defaultSortId);

    const observerTarget = useRef<IntersectionObserver | null>(null);
    const rowsListRef = useRef(null);

    const lastElementRef = useCallback((node: HTMLTableRowElement | null) => {
        if (!hasNextPage || !observerTarget || !rowsListRef) return;

        if (observerTarget.current) observerTarget.current.disconnect();
        observerTarget.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setPage((prev) => prev + 1);
            }
        });
        if (node) observerTarget.current.observe(node);
    }, [hasNextPage, setPage]);

    const sortByHandler = (colId: string) => {
        if (!colId) throw new Error("Missing argument!");

        let sortDirection_local = sortDirection === SORT_DIR.DESC ? SORT_DIR.ASC : SORT_DIR.DESC;

        if (colId !== sortBy) {
            sortDirection_local = SORT_DIR.DESC;
        }

        // TODO: add logic to reset sort to "default" at 3rd click (neither desc or asc)
        setSortDirection(sortDirection_local);
        setSortBy(colId);

        getTableData({ sortBy: colId, sortDirection: sortDirection_local })
    }

    return (
        <table className={cn("table border-transparent c-table", tableClassName)}>
            <thead className="sticky top-0 left-0">
                <tr>
                    {headCols && headCols.map(({ label, id, icon, isSort }) => (
                        <th key={id} scope="col" className="table-header__wrapper grow" onClick={() => sortByHandler(id)}>
                            <div className="flex items-center justify-center p-2 border border-white rounded darkerGray h-[42px]">
                                <i className={`table-header__text-icon ${icon} me-2 text-lg`}></i>

                                <p className="table-header__text">{label} </p>

                                {(isSort && sortBy !== id) && (
                                    <i className={`table-header__icon ps-1 text-sm bi bi-dash text-slate-950`}></i>
                                )}

                                {(isSort && sortBy === id) && (
                                    <i className={`table-header__icon ps-1 text-sm text-slate-950 bi ${sortDirection === SORT_DIR.DESC ? "bi-caret-down" : "bi-caret-up"}`}></i>
                                )}

                            </div>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody ref={rowsListRef}>
                {Array.isArray(bodyData) && bodyData.map((rowData, index) => (
                    <tr ref={lastElementRef} key={index} className="h-full">
                        {bodyCols && bodyCols.map(({ colId, dataId }) => (
                            <td key={colId} className={`table-body__wrapper align-middle border border-white lightGray rounded cursor-pointer `} >
                                <div className="flex flex-col h-full">
                                    <div className="h-full overflow-hidden rounded">
                                        {colId === 'actions' && (
                                            <div className={`flex items-center justify-center h-full ${applyDefaultCellStyles ? 'p-1 text-center' : ''}`}>{colId === 'actions' && actionsColumn(rowData.id)}</div>
                                        )}

                                        {colId !== 'actions' && (
                                            <div className={`table-body__text h-full ${applyDefaultCellStyles ? 'p-1 flex items-center justify-center' : ''}`}>{rowData[dataId]}</div>
                                        )}
                                    </div>
                                </div>
                            </td>
                        ))}
                    </tr>
                ))}
                {(Array.isArray(bodyData) && (bodyData.length === 0) && emptyTableMessage) && (
                    <tr>
                        <td colSpan={bodyCols.length} className="text-center">
                            {emptyTableMessage}
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    )
}

type Table_NEWProps<T> = {
    table: TanstackTable<T>,
    tableClassName?: string,
    tableCellClassName?: ClassValue | ClassValue[],
    staticWidth?: boolean
    data?: unknown
    page?: number
    search?: string
}

export function Table_NEW<T>({ table, tableCellClassName, tableClassName, staticWidth = false }: Table_NEWProps<T>) {
    // const [parent] = useAutoAnimate();
    const noData = table.getRowModel()?.rows?.length ? false : true;
    return (
        <table className={cn(`!table table-fixed w-full ${noData ? 'h-full':'h-min'}`, tableClassName)} {
            ...{
                style: {
                    width: staticWidth ? table.getCenterTotalSize() : undefined
                }
            }
        }>
            <thead className='cursor-pointer h-min'>
                {
                    table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id} className='h-1'>
                            {headerGroup.headers.map(header => (
                                <th
                                    key={header.id}
                                    className='cursor-pointer h-[inherit]'
                                    style={{
                                        width: header.column.columnDef.size !== undefined ? header.getSize() : 'auto',
                                    }}
                                >
                                    <div className="flex items-center justify-center h-full p-2 border rounded darkerGray border-1 border-light">
                                        {
                                            header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())
                                        }
                                    </div>
                                </th>
                            ))}
                        </tr>
                    ))
                }
            </thead>
            <tbody 
            // ref={parent}
             className='h-full'>
                {
                    table.getRowModel()?.rows.map(row => (
                        <tr key={row.id} 
                        className='h-1'
                        >
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id} className='h-[inherit] align-middle p-0'
                                    style={{
                                        minWidth: cell.column.columnDef.minSize,
                                        maxWidth: cell.column.columnDef.maxSize,
                                        width: cell.column.columnDef.size !== undefined ? cell.column.getSize() : 'auto',
                                    }}
                                >
                                    <div className={cn("h-full p-2 border border-white rounded lightGray", tableCellClassName)}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </div>
                                </td>
                            ))}
                        </tr>
                    ))
                }
                {noData && (
                    <tr className='h-full' >
                        <td className='h-[inherit] align-middle p-0 ' colSpan={table.getHeaderGroups().map(headerGroup => headerGroup.headers.length).sort((a, b) => a - b)[0]}>
                            <div className={cn("h-full p-2 border border-white rounded lightGray grid place-items-center")}>
                                <p className='italic'>No data</p>
                            </div>
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    )
}